// src/features/captions/components/StreamTermsCard.tsx

import { useMemo, useState } from 'react';
import { Button, Card, Divider, Group, Select, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useStreams } from '@/features/streams/useStreams';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { CaptionTermUtils } from '../CaptionTermUtils';
import { useCaptionTermSets } from '../useCaptionTerms';
import {
  useDeleteStreamCaptionTerms,
  useSaveStreamCaptionTerms,
} from '../useCaptionMutations';
import { CaptionTermsEditor } from './CaptionTermsEditor';

/** Bound options cap for the stream picker (stream lists are small, but never
 *  hand a combobox an uncapped array — CLAUDE.md combobox rule). */
const STREAM_PICKER_MAX_OPTIONS = 100;

/**
 * Per-stream term sets: streams with extra vocabulary of their own (a visiting
 * team's roster, a guest speaker) get a set scoped to that stream.
 */
export function StreamTermsCard() {
  const { termSets } = useCaptionTermSets();
  const { streams } = useStreams();

  // Server DELETE is a tombstone by contract: it clears the set to empty but
  // KEEPS the row so `updated` (and the caption agent's config_version) moves
  // forward. An empty media_stream set is therefore "removed" — filter it out
  // or every removed stream reappears as a 0-term row forever.
  const streamSets = useMemo(
    () => termSets.filter((s) => s.scope_type === 'media_stream' && s.terms.length > 0),
    [termSets],
  );

  // Streams picked for a new (not yet saved) set — cleared from this list once
  // a save lands and the set arrives from the server.
  const [addedStreamIds, setAddedStreamIds] = useState<number[]>([]);

  const streamTitleById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of streams) map.set(s.media.mediaId, s.media.title);
    return map;
  }, [streams]);

  const existingIds = useMemo(
    () => new Set(streamSets.map((s) => s.scope_id)),
    [streamSets],
  );

  const rows = [
    ...streamSets.map((set) => ({ mediaId: set.scope_id, serverTerms: set.terms, isNew: false })),
    ...addedStreamIds
      .filter((id) => !existingIds.has(id))
      .map((id) => ({ mediaId: id, serverTerms: [] as string[], isNew: true })),
  ];

  const shownIds = new Set(rows.map((r) => r.mediaId));
  const addOptions = streams
    .filter((s) => !shownIds.has(s.media.mediaId))
    .slice(0, STREAM_PICKER_MAX_OPTIONS)
    .map((s) => ({ value: String(s.media.mediaId), label: s.media.title }));

  const streamLabel = (mediaId: number) =>
    streamTitleById.get(mediaId) ?? `Stream ${bcLong.getStr(mediaId)}`;

  return (
    <Card shadow="sm" padding="lg" withBorder>
      <Stack gap="md">
        <div>
          <Text fw={600}>Stream Terms</Text>
          <Text size="sm" c="dimmed">
            Extra terms for a specific stream — visiting rosters, guest names, event
            vocabulary. Account terms apply to every stream; add a stream set only
            when a stream needs more.
          </Text>
        </div>

        <Select
          placeholder="Add terms for a stream..."
          data={addOptions}
          value={null}
          onChange={(val) => {
            if (!val) return;
            const id = Number(val);
            setAddedStreamIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
          }}
          searchable
          clearable={false}
          nothingFoundMessage="No matching streams"
          maw={400}
        />

        {rows.length === 0 ? (
          <Text size="sm" c="dimmed">No stream-specific terms yet.</Text>
        ) : (
          rows.map((row, i) => (
            <Stack gap="md" key={row.mediaId}>
              {i > 0 && <Divider />}
              <StreamTermSetRow
                mediaId={row.mediaId}
                label={streamLabel(row.mediaId)}
                serverTerms={row.serverTerms}
                isNew={row.isNew}
                onDiscardNew={() =>
                  setAddedStreamIds((prev) => prev.filter((id) => id !== row.mediaId))
                }
                onServerWrite={() =>
                  setAddedStreamIds((prev) => prev.filter((id) => id !== row.mediaId))
                }
              />
            </Stack>
          ))
        )}
      </Stack>
    </Card>
  );
}

interface StreamTermSetRowProps {
  mediaId: number;
  label: string;
  serverTerms: string[];
  /** True while the set exists only locally (picked, never saved). */
  isNew: boolean;
  onDiscardNew: () => void;
  /**
   * Called after a save or remove lands on the server. The parent prunes this
   * stream from its picked-this-session list — a stale entry there would
   * resurface a removed set as an empty "new" row (the tombstone filter hides
   * the server row, so the local entry is no longer masked by it).
   */
  onServerWrite: () => void;
}

function StreamTermSetRow({ mediaId, label, serverTerms, isNew, onDiscardNew, onServerWrite }: StreamTermSetRowProps) {
  const saveMutation = useSaveStreamCaptionTerms();
  const deleteMutation = useDeleteStreamCaptionTerms();

  // null = not edited; server terms flow through untouched.
  const [draft, setDraft] = useState<string[] | null>(null);
  const terms = draft ?? serverTerms;
  const isDirty = draft !== null && !CaptionTermUtils.sameTerms(draft, serverTerms);

  const handleSave = async () => {
    try {
      const res = await saveMutation.mutateAsync({ mediaId, terms });
      setDraft(null);
      onServerWrite();
      notifications.show({
        title: 'Saved',
        message: `${label}: ${CaptionTermUtils.formatWriteFeedback(res)}`,
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save stream terms',
        color: 'red',
      });
    }
  };

  const handleDelete = () => {
    if (isNew) {
      onDiscardNew();
      return;
    }
    modals.openConfirmModal({
      title: 'Remove stream terms?',
      children: (
        <Text size="sm">
          Remove all {serverTerms.length} term{serverTerms.length === 1 ? '' : 's'} for
          "{label}"? Account terms still apply to this stream.
        </Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(mediaId);
          setDraft(null);
          onServerWrite();
          notifications.show({ title: 'Removed', message: `Stream terms removed for "${label}".`, color: 'green' });
        } catch (err) {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to remove stream terms',
            color: 'red',
          });
        }
      },
    });
  };

  return (
    <Stack gap="sm">
      <CaptionTermsEditor label={label} value={terms} onChange={setDraft} />
      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          color="red"
          size="compact-sm"
          onClick={handleDelete}
          loading={deleteMutation.isPending}
        >
          {isNew ? 'Discard' : 'Remove'}
        </Button>
        {isDirty && (
          <Button variant="default" size="compact-sm" onClick={() => setDraft(null)}>
            Discard changes
          </Button>
        )}
        <Button
          size="compact-sm"
          onClick={handleSave}
          disabled={isNew ? terms.length === 0 : !isDirty}
          loading={saveMutation.isPending}
        >
          Save
        </Button>
      </Group>
    </Stack>
  );
}
